import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IgAutocomplete } from "@intelligems/ig-theme";
import {
  AnalyticsResourceType,
  ExperienceDto,
  ExperienceListDto,
} from "@intelligems/ig-types";
import { Grid, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";

import { HeaderNavigationBtns } from "../../../components/Utils/HeaderNavigationBtns";
import { setDashboardFilter } from "../../../store/slices/dashboardFilters";
import { initialDashboardFiltersState } from "../../../store/types/dashboardFilters";
import { ExperienceFe } from "../../../types/schemas/experiences";

interface ResourceListMenuProps {
  currentResource: ExperienceFe;
  activeResources: ExperienceListDto[] | ExperienceDto[];
  resourceType: AnalyticsResourceType;
}

const PREFIX = "ResourceListMenu";

const classes = {
  orgName: `${PREFIX}-orgName`,
  textfield: `${PREFIX}-textfield`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.orgName}`]: {
    fontSize: "18px",
    fontWeight: 600,
  },
  [`& .${classes.textfield}`]: {
    "& .MuiInputBase-input": {
      fontSize: "18px",
      fontWeight: 600,
      "&:hover": { cursor: "pointer" },
    },
    "& .MuiInput-underline:before": { border: "none" },
  },
}));
const FONT_SIZE = 9;
const DEFAULT_INPUT_WIDTH = 300;

export const ResourceListMenu = ({
  activeResources,
  currentResource,
  resourceType,
}: ResourceListMenuProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSwitchResource = (resourceId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("experienceId", resourceId);
    navigate(`${url.pathname}${url.search}`);
    dispatch(
      setDashboardFilter({
        ...initialDashboardFiltersState,
        experienceId: resourceType === "campaigns" ? resourceId : undefined,
      })
    );
  };

  const [inputWidth, setInputWidth] = useState(DEFAULT_INPUT_WIDTH);

  useEffect(() => {
    if (currentResource.name.length * FONT_SIZE > DEFAULT_INPUT_WIDTH) {
      setInputWidth((currentResource.name.length + 1) * FONT_SIZE);
    } else {
      setInputWidth(DEFAULT_INPUT_WIDTH);
    }
  }, [currentResource.name]);

  return (
    <StyledGrid
      item
      container
      alignItems={"center"}
      direction={"row"}
      justifyContent={"space-between"}
      xs={6}
      columnSpacing={1}
    >
      <Grid item>
        <IgAutocomplete
          onChange={(e, newValue) => {
            handleSwitchResource(newValue.value);
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable={true}
          componentsProps={{
            paper: {
              sx: {
                "&.MuiPaper-root": {
                  width: "300px",
                },
              },
            },
          }}
          value={{
            value: currentResource.id,
            label: currentResource.name,
          }}
          options={activeResources.map((resource) => ({
            value: resource.id,
            label: resource.name,
          }))}
          getOptionLabel={(option) => option.label}
          renderOption={(props, option) => (
            <li
              {...props}
              onClick={() => {
                handleSwitchResource(option.value);
              }}
              key={option.value}
            >
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ width: `${inputWidth}px` }}
              className={clsx(classes.orgName, classes.textfield)}
              variant={"standard"}
            />
          )}
        />
      </Grid>

      {resourceType === "experiences" && (
        <HeaderNavigationBtns
          currentExperience={currentResource}
          selected={"analytics"}
        />
      )}
    </StyledGrid>
  );
};
